import { ChevronRight, Dot } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";
import { HeroCard } from "../../components/HeroCard";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import { JOY_TINT } from "../../lib/joy";
import { ziggySrc, type ZiggyAge } from "../../lib/ziggy";
import { useZiggyVariant } from "../../lib/ziggyContext";
import {
  COACHING_BATCHES,
  COACHING_DOUBTS,
  COACHING_RANK,
  COACHING_TESTS,
} from "../../lib/verticalData";

const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  live: { label: "Live", className: "bg-joy-rose/15 text-joy-rose" },
  upcoming: { label: "Upcoming", className: "bg-joy-indigo/12 text-joy-indigo" },
  done: { label: "Attempted", className: "bg-joy-green/12 text-joy-green" },
};

/** Coaching-institute learner home — rank + batches + test-series forward. */
export function CoachingHome({
  firstName,
  ziggyAge = "teen",
}: {
  firstName: string;
  ziggyAge?: ZiggyAge;
}) {
  const DoubtsIcon = COACHING_DOUBTS.icon;
  const { variant } = useZiggyVariant();

  return (
    <div className="space-y-5 lg:space-y-6">
      <HeroCard
        variant="student"
        title={<>Hi, {firstName}!</>}
        subtitle="JEE Advanced 2027 · keep the rank climbing"
        mascotSrc={ziggySrc(variant, ziggyAge, "excited")}
        mascotAlt="Ziggy cheering you on"
      >
        <div className="flex flex-wrap gap-2">
          {COACHING_RANK.map((r) => (
            <span
              key={r.label}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold backdrop-blur-sm"
            >
              <r.icon className="size-3.5" />
              {r.label} {r.value}
            </span>
          ))}
        </div>
      </HeroCard>

      <div className="space-y-5 lg:grid lg:grid-cols-2 lg:gap-6 lg:space-y-0 lg:[&>*]:min-w-0">
        {/* Rank breakdown */}
        <Reveal index={1} className="lg:col-span-2">
          <SectionHeader title="My Standing" action="Analytics" />
          <div className="grid grid-cols-3 gap-3">
            {COACHING_RANK.map((r) => (
              <SectionCard key={r.label} className="flex flex-col items-start gap-2">
                <span
                  className={cn("grid size-9 place-items-center rounded-xl", JOY_TINT[r.color])}
                >
                  <r.icon className="size-5" />
                </span>
                <div className="leading-tight">
                  <p className="font-display text-lg font-semibold tabular-nums">{r.value}</p>
                  <p className="text-muted-foreground text-[0.68rem]">{r.label}</p>
                </div>
                {r.delta && (
                  <span className="text-joy-green text-[0.68rem] font-semibold">{r.delta}</span>
                )}
              </SectionCard>
            ))}
          </div>
        </Reveal>

        {/* Batches */}
        <Reveal index={2}>
          <SectionHeader title="My Batches" action="View all" />
          <div className="space-y-3">
            {COACHING_BATCHES.map((b) => (
              <SectionCard key={b.name} className="flex items-center gap-4">
                <span
                  className={cn(
                    "grid size-12 shrink-0 place-items-center rounded-2xl",
                    JOY_TINT[b.color],
                  )}
                >
                  <b.icon className="size-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold">{b.name}</p>
                  <p className="text-muted-foreground truncate text-xs">{b.course}</p>
                  <p className="text-primary mt-1 truncate text-xs font-medium">{b.nextClass}</p>
                </div>
                <ChevronRight className="text-muted-foreground size-5 shrink-0" />
              </SectionCard>
            ))}
          </div>
        </Reveal>

        {/* Test series */}
        <Reveal index={3}>
          <SectionHeader title="Test Series" action="View all" />
          <SectionCard className="divide-border/60 divide-y p-0">
            {COACHING_TESTS.map((t) => {
              const s = STATUS_STYLE[t.status]!;
              return (
                <div key={t.title} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{t.title}</p>
                    <p className="text-muted-foreground flex items-center gap-0.5 text-xs">
                      {t.when} <Dot className="size-3" /> {t.questions} Q
                      {t.score ? ` · ${t.score}` : ""}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-full px-2.5 py-1 text-[0.68rem] font-semibold",
                      s.className,
                    )}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </SectionCard>
        </Reveal>

        {/* Doubts */}
        <Reveal index={4} className="lg:col-span-2">
          <SectionCard className="hero-student hero-orbs relative overflow-hidden border-0 text-white">
            <div className="relative z-10 flex items-center gap-4">
              <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm">
                <DoubtsIcon className="size-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg font-semibold leading-tight">Doubt Box</p>
                <p className="text-sm text-white/85">
                  {COACHING_DOUBTS.open} open · {COACHING_DOUBTS.resolvedThisWeek} solved this week
                </p>
              </div>
              <button
                type="button"
                className="bg-card text-primary shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold shadow-[var(--shadow-sm)] transition-transform duration-150 ease-out active:scale-[0.97]"
              >
                Ask
              </button>
            </div>
          </SectionCard>
        </Reveal>
      </div>
    </div>
  );
}
